import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { DatepickerComponent, DropdownSelectComponent, ButtonComponent } from '@ppl/ui';
import { AddressComponent } from '../../../../shared/components/address/address.component';
import { RelationshipInformation } from '../../../../interfaces/referralSections.interface';
import { LookupService } from 'src/app/services/lookup.service';
import { DialogConfig, DialogService } from '../../../../shared/services/dialog.service';
import { parentRecordHasData } from './parent-guardian.util';

/** Max parent/guardian entries a minor participant may have (2 base + up to 2 additional). */
export const MAX_PARENT_GUARDIANS = 4;

/**
 * Factory shared by the parent (init/load) and this child (add) so a row's shape is defined once.
 * Person control names mirror the saveParticipantEnrollmentInfo payload; the address is a nested
 * group using the ppl-address control names (mapped to/from the flat payload keys by the parent and
 * steps.component). Only the second parent (index 1) carries the frontend-only `notApplicable` flag,
 * toggled ON by default.
 */
export function buildParentGuardianRow(
  fb: FormBuilder,
  data?: Partial<RelationshipInformation>,
  isSecond = false
): FormGroup {
  return fb.group({
    id: [data?.id ?? 0],
    participantId: [data?.participantId ?? null],
    fname: [data?.fname ?? ''],
    mname: [data?.mname ?? ''],
    lname: [data?.lname ?? ''],
    dob: [data?.dob ?? null],
    relationToParticipant: [data?.relationToParticipant ?? ''],
    // Address fields now match the payload names one-to-one; ppl-address binds this group directly.
    address: fb.group({
      street1: [data?.street1 ?? data?.address?.street1 ?? ''],
      street2: [data?.street2 ?? data?.address?.street2 ?? ''],
      city: [data?.city ?? data?.address?.city ?? ''],
      state: [data?.state ?? data?.address?.state ?? ''],
      zipCode: [data?.zipCode ?? data?.address?.zipCode ?? ''],
      countyOrRegion: [data?.countyOrRegion ?? data?.address?.countyOrRegion ?? ''],
      psd_code: [''],
      mailing: [false],
    }),
    // Backend passthrough kept as hidden controls so updates don't drop them.
    status: [data?.status ?? null],
    comments: [data?.comments ?? null],
    isActive: [data?.isActive ?? true],
    ...(isSecond ? { notApplicable: [data?.notApplicable ?? true] } : {}),
  });
}

@Component({
  selector: 'ppl-parent-guardian',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzFormModule,
    NzGridModule,
    NzInputModule,
    NzCheckboxModule,
    NzIconModule,
    NzButtonModule,
    NzToolTipModule,
    DatepickerComponent,
    DropdownSelectComponent,
    ButtonComponent,
    AddressComponent,
  ],
  templateUrl: './parent-guardian.component.html',
  styleUrl: './parent-guardian.component.css',
})
export class ParentGuardianComponent {
  /** FormArray owned by the parent's infoForm; this component only renders/mutates it. */
  @Input() parents!: FormArray;
  /** Per-field error messages for person fields, keyed `relationshipInformation_{i}_{field}`. */
  @Input() validationErrors: Record<string, string> = {};
  /** Per-row address error bags (index → { street1, city, ... }) for the ppl-address component. */
  @Input() addressErrors: Record<number, Record<string, string>> = {};
  @Input() readonly = false;
  @Input() mode: 'edit' | 'read' = 'edit';
  /**
   * Persisted record ids (id > 0) the user just removed — via the delete icon or by ticking
   * "Not Applicable". The host owns the API call because only it knows programCode/participantId;
   * this component stays a pure renderer of the FormArray.
   */
  @Output() deleteSavedRecords = new EventEmitter<number[]>();
  @ViewChild('confirmDeleteDialog') confirmDeleteDialog!: TemplateRef<any>;
  relationshipOptions: any[] = [];
  /** Name shown in the confirmation prompt for the record about to be removed. */
  pendingDeleteName = '';
  /** Whether that record is persisted — drives the "deleted immediately" wording. */
  pendingDeleteIsSaved = false;

  readonly maxParents = MAX_PARENT_GUARDIANS;
  today = new Date();

  constructor(
    private fb: FormBuilder,
    private lookupService: LookupService,
    private dialogSvc: DialogService,
  ) {
    this.relationshipOptions = this.lookupService.getLookupValue('Relation_Parent');
  }

  get rows(): FormGroup[] {
    return (this.parents?.controls ?? []) as FormGroup[];
  }

  addressGroup(index: number): FormGroup {
    return this.rows[index]?.get('address') as FormGroup;
  }

  /** True only for a row that carries the "Not Applicable" checkbox (the second parent). */
  hasNotApplicable(index: number): boolean {
    return !!this.rows[index]?.get('notApplicable');
  }

  isNotApplicable(index: number): boolean {
    return !!this.rows[index]?.get('notApplicable')?.value;
  }

  /**
   * Add more parents only when a real second parent exists — i.e. the "Not Applicable" checkbox on
   * the second row is unchecked (you can't have a 3rd/4th parent without a 2nd) — and below the max.
   */
  get canAddParent(): boolean {
    return this.parents?.length < this.maxParents && !this.isNotApplicable(1);
  }

  /**
   * A 3rd/4th parent can only exist alongside a real second parent, so "Not Applicable" is locked
   * while any additional rows are present — the user must delete those first. Mirrors canAddParent,
   * which blocks adding while NA is ticked.
   */
  get isNotApplicableDisabled(): boolean {
    return this.readonly || this.parents?.length > 2;
  }

  addParent(): void {
    if (this.parents.length < this.maxParents) {
      this.parents.push(buildParentGuardianRow(this.fb));
      this.parents.markAsDirty();
    }
  }

  removeParent(index: number): void {
    // Only the additional parents (index >= 2) can be removed; the two base entries stay.
    if (index < 2 || this.parents.length <= 2) {
      return;
    }

    // An untouched row holds nothing worth confirming — dropping it costs nothing.
    this.confirmBeforeRemove(index, () => {
      this.emitSavedIdFor(index);
      this.parents.removeAt(index);
      this.parents.markAsDirty();
    });
  }

  /**
   * Ticking "Not Applicable" discards the second parent, so a persisted record has to go too.
   * Unticking is not a delete — nothing was removed.
   */
  onNotApplicableChange(index: number, checked: boolean): void {
    // Defence in depth for [nzDisabled]: nz-checkbox's ControlValueAccessor also writes nzDisabled,
    // so the binding can lose a race on init. Reject the change outright rather than rely on the
    // control looking disabled.
    if (checked && this.isNotApplicableDisabled) {
      this.rows[index]?.get('notApplicable')?.setValue(false, { emitEvent: false });
      return;
    }

    if (!checked) {
      this.parents.markAsDirty();
      return;
    }

    this.confirmBeforeRemove(
      index,
      () => {
        this.emitSavedIdFor(index);
        this.parents.markAsDirty();
      },
      // Declined — untick, because nz-checkbox has already flipped the control's value.
      () => this.rows[index]?.get('notApplicable')?.setValue(false, { emitEvent: false }),
    );
  }

  /** True when the row holds any person or address value — i.e. removing it loses something. */
  private rowHasData(index: number): boolean {
    const raw: any = this.rows[index]?.getRawValue() ?? {};
    const { address, ...person } = raw;
    // parentRecordHasData expects the flat payload shape, so fold the nested address group in.
    return parentRecordHasData({ ...person, ...(address ?? {}) });
  }

  /**
   * Runs `proceed` straight away for an empty, never-saved row; otherwise asks first. Prompting on
   * data (not just on id > 0) covers the second parent typed in but not yet saved, where ticking
   * "Not Applicable" would silently discard the entry.
   */
  private confirmBeforeRemove(index: number, proceed: () => void, onDecline: () => void = () => undefined): void {
    const savedId = this.savedIdFor(index);
    if (savedId === 0 && !this.rowHasData(index)) {
      proceed();
      return;
    }

    const row = this.rows[index];
    const name = [row?.get('fname')?.value, row?.get('lname')?.value].filter(Boolean).join(' ').trim();
    this.pendingDeleteName = name || `Parent/Guardian ${index + 1}`;
    // Only a persisted record is deleted server-side; an unsaved one is just discarded.
    this.pendingDeleteIsSaved = savedId > 0;

    const dialogConfig: DialogConfig = {
      title: 'Confirmation',
      okText: 'Remove',
      size: 'medium',
      position: 'top',
      showClose: true,
      showButtons: true,
      // ppl-dialog and ppl-popup-dialog both hard-code z-index 1050, so this confirmation ties with
      // the Parent/Guardian dialog it is opened from and can paint behind it. See the
      // .ppl-confirm-above-dialog rule in assets/styles/styles.css.
      styleClass: 'ppl-confirm-above-dialog',
    };
    this.dialogSvc.openDialog(this.confirmDeleteDialog, dialogConfig, proceed, onDecline);
  }

  private savedIdFor(index: number): number {
    return Number(this.rows[index]?.get('id')?.value ?? 0) || 0;
  }

  /** Emits the row's id only when it is persisted (id > 0); brand-new rows need no API call. */
  private emitSavedIdFor(index: number): void {
    const id = this.savedIdFor(index);
    if (id > 0) {
      this.deleteSavedRecords.emit([id]);
    }
  }

  errorFor(index: number, field: string): string {
    return this.validationErrors?.[`relationshipInformation_${index}_${field}`] || '';
  }

  addressErrorsFor(index: number): Record<string, string> {
    return this.addressErrors?.[index] || {};
  }
}
